import { useCallback, useEffect, useState } from "react";
import { onFcmForegroundMessage } from "@/lib/fcm";
import { getNotices, putNotice, type StoredNotice } from "@/lib/noticeStore";

export type WorkerNotice = StoredNotice;

interface NoticePayload {
  title?: string;
  body?: string;
  data?: { siteUuid?: string; title?: string; body?: string; message?: string };
}

function toNotice(p: NoticePayload, uuid: string): WorkerNotice {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: p.title || p.data?.title || "현장 관리자 안내",
    message: p.body || p.data?.body || p.data?.message || "",
    receivedAt: new Date().toISOString(),
    siteUuid: p.data?.siteUuid || uuid,
  };
}

export function useWorkerNotices(uuid: string) {
  const [notices, setNotices] = useState<WorkerNotice[]>([]);
  const [banner, setBanner] = useState<WorkerNotice | null>(null);

  const refresh = useCallback(async () => {
    if (!uuid) {
      setNotices([]);
      return;
    }
    setNotices(await getNotices(uuid));
  }, [uuid]);

  useEffect(() => {
    setBanner(null);
    let cancelled = false;
    (async () => {
      const list = uuid ? await getNotices(uuid) : [];
      if (!cancelled) setNotices(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const accept = useCallback(
    async (p: NoticePayload) => {
      if (!uuid) return;
      const siteUuid = p.data?.siteUuid;
      if (siteUuid && siteUuid !== uuid) return;
      const notice = toNotice(p, uuid);
      await putNotice(notice);
      setBanner(notice);
      await refresh();
    },
    [uuid, refresh]
  );

  useEffect(() => {
    if (!uuid || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    function onMessage(e: MessageEvent) {
      const data = e.data as { type?: string; payload?: NoticePayload } | null;
      if (!data || data.type !== "safers-notice") return;
      const p = data.payload ?? {};
      if (p.data?.siteUuid && p.data.siteUuid !== uuid) return;
      setBanner(toNotice(p, uuid));
      refresh();
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [uuid, refresh]);

  useEffect(() => {
    if (!uuid) return;
    let unsub: (() => void) | undefined;
    try {
      unsub = onFcmForegroundMessage((payload) => {
        accept({
          title: payload.notification?.title,
          body: payload.notification?.body,
          data: payload.data as NoticePayload["data"],
        });
      });
    } catch {
      unsub = undefined;
    }
    return () => unsub?.();
  }, [uuid, accept]);

  const dismissBanner = useCallback(() => setBanner(null), []);

  return { notices, banner, dismissBanner };
}
